import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';
import { GraphQlService } from '@shared/services/graphql-service';
import {
  ADD_BRELA_LICENSE,
  GENERATE_CONTROL_NUMBER,
  GET_BILL_BY_UUID,
  GET_OMC_REGISTRATION_PROGRESS,
  OMC_REGISTRATION_SUMMARY,
  REGISTER_OMC_MUTATION,
  SUBMIT_OMC_APPLICATION,
  SUBMIT_OMC_DOCUMENTS,
  VERIFY_BRELA_LICENSE,
  VERIFY_EWURA_LICENSE,
  VERIFY_IMPORT_LICENSE,
  VIEW_OMC_DOCUMENTS,
} from '../graphql/omc-registration.graphql';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import { OmcRegistrationResponse } from '../responses/omc-registration.response';
import { OmcEwuraLicenseResponse } from '../responses/ewura.license';
import { OmcRegistrationProgressResponse } from '../responses/omc-registration-progress';
import { OmcAttachmentResponse } from '../responses/omc-attachment.response';
import { OmcRegistrationSummaryResponse } from '../responses/omc-registration-summary.response';
import { SubmitOmcApplicationResponse } from '../responses/omc-submit-application.response';
import { ViewAttachmentResponse } from '../responses/view-attachment.response';
import { GenerateControlNumberResponse } from '../responses/generate,control-number.response';
import { GetBillByUuidResponse } from '../responses/bill-by-uuid.response';
import { VERIFY_BRELA_REGISTRATION } from '@modules/company-profile/graphql/company-profile.graphql';
import { BrelaLicenseResponse } from '../responses/brela-license.response';
import { AddBrelaLicenseResponse } from '../responses/add-brela-license.response';
import * as OmcRegistrationActions from '../store/omc-registration.action';
import {
  GET_OMC_CONTRACT,
  GET_OMC_CONTRACT_BY_UUID,
} from '@modules/applications/omc-applications/graphql/omc-applications.graphql';
import { GetOmcContractResponse } from '../responses/fetch-contract.response';
import { ImportLicenseResponse, VerifyImportLicenseResponse } from '../responses/importation-license-verification.response';

@Injectable({
  providedIn: 'root',
})
export class OmcRegistrationEffects {
  constructor(
    private actions$: Actions,
    private graphQlService: GraphQlService,
  ) {}

  $saveCompanyInformations = createEffect(() =>
    this.actions$.pipe(
      ofType(OmcRegistrationActions.OmcRegActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<OmcRegistrationResponse>({
            mutation: REGISTER_OMC_MUTATION,
            variables: { input: request },
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response.data.registerOmc;
              if (!payload.status) {
                return OmcRegistrationActions.OmcRegActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Registration failed',
                    data: null,
                  },
                });
              }
              return OmcRegistrationActions.OmcRegActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                OmcRegistrationActions.OmcRegActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Registration failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $verifyEwuraLicence = createEffect(() =>
    this.actions$.pipe(
      ofType(OmcRegistrationActions.EwuraLicenseActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<OmcEwuraLicenseResponse>({
            mutation: VERIFY_EWURA_LICENSE,
            variables: { input: request },
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response.data.verifyEwuraLicense;
              if (!payload.status) {
                return OmcRegistrationActions.EwuraLicenseActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Verification failed',
                    data: null,
                  },
                });
              }
              return OmcRegistrationActions.EwuraLicenseActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                OmcRegistrationActions.EwuraLicenseActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Verification failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $verifyBrelaLicence = createEffect(() =>
    this.actions$.pipe(
      ofType(OmcRegistrationActions.BrelaLicenseActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<BrelaLicenseResponse>({
            mutation: VERIFY_BRELA_LICENSE,
            variables: { input: request },
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response.data.verifyBrelaLicense;
              if (!payload.status) {
                return OmcRegistrationActions.BrelaLicenseActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Verification failed',
                    data: null,
                  },
                });
              }
              return OmcRegistrationActions.BrelaLicenseActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                OmcRegistrationActions.BrelaLicenseActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Verification failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $verifyImportLicence = createEffect(() =>
    this.actions$.pipe(
      ofType(OmcRegistrationActions.ImportLicenseActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<VerifyImportLicenseResponse>({
            mutation: VERIFY_IMPORT_LICENSE,
            variables: { input: request },
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response.data.verifyImportationLicence;
              if (!payload.status) {
                return OmcRegistrationActions.ImportLicenseActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Verification failed',
                    data: null,
                  },
                });
              }
              return OmcRegistrationActions.ImportLicenseActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                OmcRegistrationActions.ImportLicenseActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Verification failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $addBrelaLicence = createEffect(() =>
    this.actions$.pipe(
      ofType(OmcRegistrationActions.BrelaSubmitActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<AddBrelaLicenseResponse>({
            mutation: ADD_BRELA_LICENSE,
            variables: { input: request },
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response.data.addBrelaLicense;
              if (!payload.status) {
                return OmcRegistrationActions.BrelaSubmitActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Verification failed',
                    data: null,
                  },
                });
              }
              return OmcRegistrationActions.BrelaSubmitActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                OmcRegistrationActions.BrelaSubmitActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Verification failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $getOmcRegistrationProgress = createEffect(() =>
    this.actions$.pipe(
      ofType(OmcRegistrationActions.OmcProgressActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<OmcRegistrationProgressResponse>({
            query: GET_OMC_REGISTRATION_PROGRESS,
            variables: { omcUuid: request },
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response.data.omcRegistrationProgress;
              if (!payload.status) {
                return OmcRegistrationActions.OmcProgressActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Verification failed',
                    data: null,
                  },
                });
              }
              return OmcRegistrationActions.OmcProgressActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                OmcRegistrationActions.OmcProgressActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Verification failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $submitOmcDocuments = createEffect(() =>
    this.actions$.pipe(
      ofType(OmcRegistrationActions.OmcDocumentActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<OmcAttachmentResponse>({
            mutation: SUBMIT_OMC_DOCUMENTS,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((response) => {
              console.log('ATTACHMENT RESPONSE::', JSON.stringify(response));
              const payload = response.data.attachOmcDocument;
              if (!payload.status) {
                return OmcRegistrationActions.OmcDocumentActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed',
                    data: null,
                  },
                });
              }
              return OmcRegistrationActions.OmcDocumentActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: 'Success',
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                OmcRegistrationActions.OmcDocumentActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $getOmcRegistrationSummary = createEffect(() =>
    this.actions$.pipe(
      ofType(OmcRegistrationActions.OmcSummaryActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<OmcRegistrationSummaryResponse>({
            query: OMC_REGISTRATION_SUMMARY,
            apolloNamespace: ApolloNamespace.registration,
            variables: { omcUuid: request },
          })
          .pipe(
            map((response) => {
              const payload = response.data.getOmcRegistration;
              if (!payload) {
                return OmcRegistrationActions.OmcSummaryActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed',
                    data: null,
                  },
                });
              }
              return OmcRegistrationActions.OmcSummaryActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: 'Success',
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                OmcRegistrationActions.OmcSummaryActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $submitOmcApplication = createEffect(() =>
    this.actions$.pipe(
      ofType(OmcRegistrationActions.OmcSubmitAppActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<SubmitOmcApplicationResponse>({
            mutation: SUBMIT_OMC_APPLICATION,
            apolloNamespace: ApolloNamespace.registration,
            variables: { input: request },
          })
          .pipe(
            map((response) => {
              const payload = response.data.submitOmcApplication;
              if (!payload.status) {
                return OmcRegistrationActions.OmcSubmitAppActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload.message,
                    data: payload,
                  },
                });
              }
              return OmcRegistrationActions.OmcSubmitAppActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message,
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                OmcRegistrationActions.OmcSubmitAppActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error ?? 'Failed',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $viewOmcAttachment = createEffect(() =>
    this.actions$.pipe(
      ofType(OmcRegistrationActions.OmcViewAttachmentActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<ViewAttachmentResponse>({
            query: VIEW_OMC_DOCUMENTS,
            apolloNamespace: ApolloNamespace.registration,
            variables: { attachmentUuid: request },
          })
          .pipe(
            map((response) => {
              //console.log('VIEW ATTACHMENTS RESPONSE::', JSON.stringify(response));
              const payload = response.data.viewAttachmentByUuid;
              if (!payload.status) {
                return OmcRegistrationActions.OmcViewAttachmentActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload.message,
                    data: null,
                  },
                });
              }
              return OmcRegistrationActions.OmcViewAttachmentActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: 'Success',
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                OmcRegistrationActions.OmcViewAttachmentActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message,
                    data: error,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $generateControlNumber = createEffect(() =>
    this.actions$.pipe(
      ofType(OmcRegistrationActions.OmcPaymentActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<GenerateControlNumberResponse>({
            mutation: GENERATE_CONTROL_NUMBER,
            apolloNamespace: ApolloNamespace.billing,
            variables: { billUuid: request.billingUuid },
          })
          .pipe(
            map((response) => {
              //console.log('GENERATE CONTROL NUMBER RESPONSE::', JSON.stringify(response));
              const payload = response.data.generateControlNumber;
              if (!payload.status) {
                return OmcRegistrationActions.OmcPaymentActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload.message,
                    data: null,
                  },
                });
              }
              return OmcRegistrationActions.OmcPaymentActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: 'Success',
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                OmcRegistrationActions.OmcPaymentActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message,
                    data: error,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $getBillByUuid = createEffect(() =>
    this.actions$.pipe(
      ofType(OmcRegistrationActions.OmcBillActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<GetBillByUuidResponse>({
            mutation: GET_BILL_BY_UUID,
            apolloNamespace: ApolloNamespace.billing,
            variables: { uuid: request.billingUuid },
          })
          .pipe(
            map((response) => {
              //console.log('BILL BY UUID RESPONSE::', JSON.stringify(response));
              const payload = response.data.getBillByUuid;
              if (!payload.status) {
                return OmcRegistrationActions.OmcBillActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload.message,
                    data: null,
                  },
                });
              }
              return OmcRegistrationActions.OmcBillActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: 'Success',
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                OmcRegistrationActions.OmcBillActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message,
                    data: error,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $getOmcContract = createEffect(() =>
    this.actions$.pipe(
      ofType(OmcRegistrationActions.OmcContractActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<GetOmcContractResponse>({
            query: GET_OMC_CONTRACT_BY_UUID,
            apolloNamespace: ApolloNamespace.registration,
            variables: { contractUuid: request },
          })
          .pipe(
            map((response) => {
              //console.log('BILL BY UUID RESPONSE::', JSON.stringify(response));
              const payload = response.data.getOmcContractByUuid;
              if (!payload.status) {
                return OmcRegistrationActions.OmcContractActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload.message,
                    data: null,
                  },
                });
              }
              return OmcRegistrationActions.OmcContractActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: 'Success',
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                OmcRegistrationActions.OmcContractActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message,
                    data: error,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );
}
